import { useClientLogger } from '@landsbankinn/client-logger';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ElectronicIdHubData } from 'src/services/ElectronicIdHubService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  WindowLocationServiceToken,
  type IWindowLocationService,
} from 'src/services/WindowLocationService';
import {
  useElectronicIdHub,
  type UseElectronicIdHubSubscriber,
} from './useElectronicIdHub';
import { useApplicationClientContext } from 'src/state';

type ElectronicIdHubType = 'PKISim' | 'AudkenniApp';
export function useElectronicIdSubscriber<T extends ElectronicIdHubData>(
  type: ElectronicIdHubType,
): { data: T | undefined } {
  const [, clientDispatch] = useApplicationClientContext();
  const navigate = useNavigate();
  const windowLocationService =
    ServicesProvider.getSingleton<IWindowLocationService>(
      WindowLocationServiceToken,
      navigate,
    );

  const { logger } = useClientLogger();
  const [data, setData] = useState<T | undefined>(undefined);

  const subscribers = useMemo<UseElectronicIdHubSubscriber<T>>(
    () => ({
      onReceiveVerificationData: (data: T) => {
        setData(data);
      },
      onReceiveSuccess: (callbackUrl: string) => {
        logger.message(`${type} success`, { callbackUrl });
        windowLocationService.goto(callbackUrl, navigate);
      },
      onReceiveError: (message: string) => {
        logger.error(new Error(`${type} error`), { message });
        clientDispatch({
          type: 'setLoginErrorState',
          payload: { errorMessage: message },
        });
      },
    }),
    [logger, navigate, type, windowLocationService, clientDispatch],
  );

  useElectronicIdHub<T>(subscribers);
  return { data };
}
